import { computed, ref, watch, type Ref } from "vue";
import { usePage } from "@inertiajs/vue3";
import type {
    IAiSlashCommand,
    IAiSlashCommandParameter,
    IAiSlashCommandSuggestion,
} from "@/types/AiChat/IAiSlashCommand";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { useTranslations } from "@/composables/useTranslations";

export const AI_SLASH_PREFIX = "/tool";

const leadingTrimmed = (draft: string): string => draft.replace(/^\s+/, "");

/**
 * Whether a draft is a typed command rather than an ordinary question. The
 * prefix has to be followed by a space or nothing at all, so a message opening
 * with "/toolbar" stays a message.
 */
export const isAiSlashCommandDraft = (draft: string): boolean => {
    const value = leadingTrimmed(draft);

    if (!value.toLowerCase().startsWith(AI_SLASH_PREFIX)) {
        return false;
    }

    const next = value.charAt(AI_SLASH_PREFIX.length);

    return next === "" || next === " ";
};

/**
 * Drives the "/" suggester shared by the full-page composer and the widget:
 * loads the server-owned catalogue, turns the current draft into the lines to
 * offer, and owns the keyboard behaviour of the popover.
 *
 * It deliberately does not validate the draft. The grammar lives on the server
 * and reporting it twice would mean two versions of the same rules — a command
 * is simply sent, and the server answers with the precise reason if it cannot
 * run it.
 */
export function useAiSlashCommands(draft: Ref<string>) {
    const { t } = useTranslations();
    const page = usePage<IRhizomPageProps>();

    // The catalogue travels with the page, so it is here on first paint: no
    // request to wait for, and nothing to load before the suggester can answer.
    // An empty list is how a disabled feature arrives.
    const commands = computed<IAiSlashCommand[]>(
        () => page.props.aiSlashCommands ?? [],
    );
    const enabled = computed(() => commands.value.length > 0);
    const dismissed = ref(false);
    const activeIndex = ref(0);

    const featured = computed<IAiSlashCommand[]>(() =>
        commands.value.filter((command) => command.featured),
    );

    const labelFor = (command: IAiSlashCommand): string =>
        t(`vue.${command.translation_key}`);

    const findCommand = (name: string): IAiSlashCommand | null =>
        commands.value.find((command) => command.command === name) ?? null;

    const usedParameterNames = (fragment: string): Set<string> => {
        const names = new Set<string>();
        for (const match of fragment.matchAll(/([a-z][a-z0-9_]*)=/g)) {
            names.add(match[1] ?? "");
        }
        return names;
    };

    const prefixSuggestion = (value: string): IAiSlashCommandSuggestion[] =>
        AI_SLASH_PREFIX.startsWith(value.toLowerCase())
            ? [
                  {
                      key: AI_SLASH_PREFIX,
                      label: AI_SLASH_PREFIX,
                      hint: t("vue.ai_slash_prefix_hint"),
                      insert: `${AI_SLASH_PREFIX} `,
                  },
              ]
            : [];

    const commandSuggestions = (typed: string): IAiSlashCommandSuggestion[] =>
        commands.value
            .filter((command) =>
                command.command.startsWith(typed.toLowerCase()),
            )
            .map((command) => ({
                key: command.command,
                label: command.command,
                hint: labelFor(command),
                insert: command.example,
            }));

    const parameterSuggestions = (
        command: IAiSlashCommand,
        value: string,
        fragment: string,
    ): IAiSlashCommandSuggestion[] => {
        const used = usedParameterNames(fragment);

        return command.parameters
            .filter((parameter) => !used.has(parameter.name))
            .map((parameter) => ({
                key: parameter.name,
                label: `${parameter.name}=`,
                hint: parameterHint(parameter),
                insert: `${value.replace(/\s+$/, "")} ${parameter.name}=${suggestedValueOf(parameter)}`,
            }));
    };

    // What the composer would write, where the value does not depend on the
    // team. A domain or an IP never gets one: filling in an example there reads
    // the wrong thing while looking right.
    const suggestedValueOf = (parameter: IAiSlashCommandParameter): string =>
        parameter.suggested_value ?? "";

    const parameterHint = (parameter: IAiSlashCommandParameter): string => {
        const requirement = parameter.required
            ? t("vue.ai_slash_param_required")
            : t("vue.ai_slash_param_optional");

        return `${requirement} · ${parameter.suggested_value ?? parameter.placeholder}`;
    };

    const suggestions = computed<IAiSlashCommandSuggestion[]>(() => {
        if (!enabled.value) {
            return [];
        }

        const value = leadingTrimmed(draft.value);

        if (!value.startsWith("/")) {
            return [];
        }

        if (!isAiSlashCommandDraft(value)) {
            return prefixSuggestion(value);
        }

        const fragment = value
            .slice(AI_SLASH_PREFIX.length)
            .replace(/^\s+/, "");
        const typed = fragment.split(/\s+/)[0] ?? "";
        const command = findCommand(typed);

        return command === null
            ? commandSuggestions(typed)
            : parameterSuggestions(command, value, fragment);
    });

    const open = computed(
        () => !dismissed.value && suggestions.value.length > 0,
    );

    watch(draft, () => {
        dismissed.value = false;
        activeIndex.value = 0;
    });

    watch(suggestions, (value) => {
        if (activeIndex.value >= value.length) {
            activeIndex.value = 0;
        }
    });

    const apply = (suggestion: IAiSlashCommandSuggestion): void => {
        draft.value = suggestion.insert;
        activeIndex.value = 0;
    };

    /**
     * Returns true when the popover consumed the key, so the composer knows not
     * to also send the message or move the caret.
     */
    const handleKeydown = (event: KeyboardEvent): boolean => {
        if (!open.value) {
            return false;
        }

        if (event.key === "Escape") {
            dismissed.value = true;
            return true;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            const step = event.key === "ArrowDown" ? 1 : -1;
            const count = suggestions.value.length;
            activeIndex.value = (activeIndex.value + step + count) % count;
            return true;
        }

        if (event.key === "Enter" || event.key === "Tab") {
            const suggestion = suggestions.value[activeIndex.value];
            if (suggestion === undefined) {
                return false;
            }
            apply(suggestion);
            return true;
        }

        return false;
    };

    return {
        enabled,
        commands,
        featured,
        suggestions,
        open,
        activeIndex,
        labelFor,
        apply,
        handleKeydown,
    };
}
